import { useCallback } from "react";
import { Switch } from "@mui/material";
import { styled } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import styles from "./CategoryEditModal.module.css";

/**
 * Compact switch used for the category and for each subcategory.
 * The track turns brand blue when the row is active.
 */
const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 46,
  height: 24,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 30,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(9px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 3,
    "&.Mui-checked": {
      transform: "translateX(23px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#1D5AE8",
        ...theme.applyStyles("dark", {
          backgroundColor: "#177ddc",
        }),
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 18,
    height: 18,
    borderRadius: 999,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: "999px",
    opacity: 1,
    backgroundColor: "rgba(0,0,0,.25)",
    boxSizing: "border-box",
    ...theme.applyStyles("dark", {
      backgroundColor: "rgba(255,255,255,.35)",
    }),
  },
}));

/**
 * Inline rename row. Save is disabled until the draft name is non-empty.
 * The parent decides whether that name collides with another subcategory.
 */
const EditableSubcategoryRow = ({
  tempSubcategory,
  index,
  onNameChange,
  onSave,
}) => {
  /**
   * Sends the rename input text to the parent draft.
   * @param {Event} event - Input change event.
   * @returns {void}
   */
  const handleNameChange = useCallback(
    (event) => {
      onNameChange(event.target.value);
    },
    [onNameChange],
  );
  /**
   * Asks the parent to save this subcategory's new name.
   * @returns {void}
   */
  const handleSave = useCallback(() => {
    onSave(index);
  }, [onSave, index]);

  return (
    <div className={styles.subcategoryItem}>
      <input
        type="text"
        value={tempSubcategory.nombre}
        onChange={handleNameChange}
      />
      <button
        className={styles.editSubcategoryButton}
        disabled={!tempSubcategory.nombre}
        onClick={handleSave}
      >
        +
      </button>
    </div>
  );
};

/**
 * Read-only subcategory. The switch toggles `activo` and the pencil swaps this row for the rename input.
 * The click handlers are memoized here so the row does not pass a fresh arrow into the switch.
 */
const SubcategoryRow = ({
  subcategoria,
  index,
  t,
  onSubcategorySwitchChange,
  onEditClick,
}) => {
  /**
   * Toggles this subcategory's active flag.
   * @returns {void}
   */
  const handleSwitchChange = useCallback(() => {
    onSubcategorySwitchChange(index);
  }, [onSubcategorySwitchChange, index]);
  /**
   * Opens this subcategory for rename.
   * @returns {void}
   */
  const handleEdit = useCallback(() => {
    onEditClick(index);
  }, [onEditClick, index]);

  return (
    <div className={styles.subcategoryItem}>
      <AntSwitch
        inputProps={{
          "aria-label": t("components.categoryEditModal.toggleSubcategoryAria", {
            name: subcategoria.nombre,
          }),
        }}
        checked={subcategoria.activo}
        onChange={handleSwitchChange}
      />
      <label>{subcategoria.nombre}</label>
      <EditIcon className={styles.actionsIcons} onClick={handleEdit} />
    </div>
  );
};

/**
 * Renders nothing when there are no subcategories.
 * The key is `clientKey`, not the array index, so reordering or inserting a row does not reuse the wrong input.
 */
const SubcategoryList = ({
  t,
  tempCategory,
  tempSubcategory,
  onNameChange,
  onSave,
  onSubcategorySwitchChange,
  onEditClick,
}) => {
  if (!tempCategory.subcategorias || tempCategory.subcategorias.length === 0) {
    return null;
  }
  return (
    <div className={styles.subcategoriesListContainer}>
      {tempCategory.subcategorias.map((subcategoria, index) =>
        subcategoria.editable ? (
          <EditableSubcategoryRow
            key={subcategoria.clientKey}
            tempSubcategory={tempSubcategory}
            index={index}
            onNameChange={onNameChange}
            onSave={onSave}
          />
        ) : (
          <SubcategoryRow
            key={subcategoria.clientKey}
            subcategoria={subcategoria}
            index={index}
            t={t}
            onSubcategorySwitchChange={onSubcategorySwitchChange}
            onEditClick={onEditClick}
          />
        ),
      )}
    </div>
  );
};

/**
 * Category edit dialog. The hook owns the draft; this view only renders it.
 * Clearing the name also clears the "category already exists" message.
 * The success toast is shown after the dialog has already closed.
 *
 * The overlay is rendered only while `open` is true. The dialog element stays mounted either way
 * so the hook can call `showModal` and `close` on the same node.
 *
 * The category save button is disabled until the name is non-empty.
 * The add-subcategory button is disabled until its input is non-empty.
 * A duplicate subcategory name is reported under the list, not inside the row.
 * The pencil and the active switch on a saved row call back with that row's index.
 * The index is only used to update that row. The React key stays `clientKey` so the input is not reused
 * when a new subcategory is inserted at the top of the list.
 * Cancel calls `onClose` directly. It does not write the draft back to the catalog.
 */
const CategoryEditModalView = (form) => (
  <>
    {form.open && <div className={styles.overlay}></div>}
    <dialog ref={form.dialogRef} className={styles.modal}>
      <div className={styles.closeButtonContainer}>
        <button className={styles.closeButton} onClick={form.onClose}>
          &times;
        </button>
      </div>

      <div className={styles.modalContent}>
        <label className={styles.modalTitle}>
          {form.t("components.categoryEditModal.title")}
        </label>
        <div className={styles.nameContainer}>
          <label className={styles.modalLabel}>
            {form.t("components.categoryEditModal.categoryLabel")}
          </label>
          <div className={styles.inputContainer}>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                placeholder={form.t(
                  "components.categoryEditModal.categoryPlaceholder",
                )}
                value={form.tempCategory.nombre}
                onChange={form.handleCategoryNameChange}
                className={styles.modalInput}
              />
              {form.tempCategory.nombre && (
                <button
                  className={styles.inputClearButton}
                  onClick={form.handleClearCategoryName}
                >
                  &times;
                </button>
              )}
            </div>
          </div>
          {form.categoryMessageError && (
            <span className={styles.errorMessage}>
              {form.categoryMessageError}
            </span>
          )}
        </div>
        <div className={styles.switchContainer}>
          <span className={styles.modalLabel}>
            {form.t("components.categoryEditModal.toggleCategoryLabel")}
          </span>
          <AntSwitch
            inputProps={{
              "aria-label": form.t(
                "components.categoryEditModal.toggleCategoryLabel",
              ),
            }}
            checked={form.tempCategory.activo}
            onChange={form.onCategorySwitchChange}
          />
        </div>
        <div className={styles.subcategoriesContainer}>
          <div className={styles.subcategoriesHeader}>
            <span className={styles.modalLabel}>
              {form.t("components.categoryEditModal.subcategoriesLabel")}
            </span>
            <span
              className={styles.modalLabelButton}
              onClick={form.handleShowCreateSubcategory}
            >
              <span>+ </span>
              {form.t("components.categoryEditModal.createSubcategory")}
            </span>
          </div>
          {form.showCreateSubcategory && (
            <div className={styles.addSubcategoryContainer}>
              <input
                type="text"
                placeholder={form.t(
                  "components.categoryEditModal.subcategoryPlaceholder",
                )}
                value={form.subcategoryName}
                onChange={form.handleSubcategoryNameChange}
              />
              <button
                className={styles.addSubcategoryButton}
                onClick={form.handleAddSubcategory}
                disabled={!form.subcategoryName}
              >
                {form.t("components.categoryEditModal.addButton")}
              </button>
            </div>
          )}
        </div>
        <SubcategoryList
          t={form.t}
          tempCategory={form.tempCategory}
          tempSubcategory={form.tempSubcategory}
          onNameChange={form.handleTempSubcategoryNameChange}
          onSave={form.handleEditSubcategory}
          onSubcategorySwitchChange={form.onSubcategorySwitchChange}
          onEditClick={form.handleEditClick}
        />
        {form.subcategoryMessageError && (
          <span className={styles.errorMessage}>
            {form.subcategoryMessageError}
          </span>
        )}
      </div>

      <div className={styles.modalButtons}>
        <button onClick={form.onClose} className={styles.cancelButton}>
          {form.t("common.cancel")}
        </button>
        <button
          className={styles.createButton}
          onClick={form.handleUpdateCategory}
          disabled={!form.tempCategory.nombre}
        >
          {form.t("components.categoryEditModal.saveButton")}
        </button>
      </div>
    </dialog>
    {form.showSuccessMessage && (
      <div className={styles.successMessage}>
        {form.t("components.categoryEditModal.successMessage")}
      </div>
    )}
  </>
);

export default CategoryEditModalView;
